import { type PoiRuleValue } from '@lib/customization/poiRules';
import { useLocalization } from '@lib/localization';
import { Box, Chip, InputAdornment, MenuItem, Select, Slider, Stack, Switch, TextField, Typography } from '@mui/material';
import { type PoiField } from '@store/api/poiTypeEndpoints';
import { memo } from 'react';
import { BarcodeIcon } from 'react-line-awesome';
import FieldHeading, { LabelText, type Languages } from './FieldHeading';

interface Props {
  field: PoiField;
  languages: Languages;
  value: PoiRuleValue;
  /** Stable across renders, so a field whose answer did not change is not drawn again. */
  onAnswer: (id: string, value: PoiRuleValue) => void;
  required: boolean;
  readOnly: boolean;
  marker?: React.ReactNode;
}

/** A field in Try it: the real control, answered the way an inspector answers it. */
const FieldInput: React.FC<Props> = ({ field, languages, value, onAnswer, required, readOnly, marker }) => {
  const { t } = useLocalization();

  const set = (next: PoiRuleValue) => field.id && onAnswer(field.id, next);
  const options = field.options ?? [];
  const choice = (id: string) => (
    <LabelText text={options.find((option) => option.id === id)?.label} empty="…" languages={languages} />
  );
  const placeholder = (text: string) => <Typography component="span" color="text.disabled" variant="body2">{text}</Typography>;

  const control = () => {
    switch (field.type) {
      case 'Range': {
        const min = field.min ?? 0;

        return (
          <Slider
            size="small" min={min} max={field.max ?? 100} step={field.decimal ? 0.1 : 1} disabled={readOnly}
            value={typeof value === 'number' ? value : min}
            onChange={(_event, next) => set(next as number)}
            sx={{ mx: 0.75, width: 'calc(100% - 12px)' }}
          />
        );
      }
      case 'List':
        return (
          <Select
            fullWidth size="small" displayEmpty disabled={readOnly}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => set(event.target.value || undefined)}
            renderValue={(id) => (id ? choice(id) : placeholder(t('Customization_Designer_Select')))}
          >
            <MenuItem value=""><em>{t('Customization_Designer_Select')}</em></MenuItem>
            {options.map((option) => <MenuItem key={option.id} value={option.id ?? ''}>{choice(option.id ?? '')}</MenuItem>)}
          </Select>
        );
      case 'MultiList':
        return (
          <Select
            fullWidth multiple size="small" displayEmpty disabled={readOnly}
            value={Array.isArray(value) ? value : []}
            onChange={(event) => set(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
            renderValue={(ids) => (ids.length
              ? <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>{ids.map((id) => <Chip key={id} size="small" label={choice(id)} />)}</Stack>
              : placeholder(t('Customization_Designer_SelectSeveral')))}
          >
            {options.map((option) => <MenuItem key={option.id} value={option.id ?? ''}>{choice(option.id ?? '')}</MenuItem>)}
          </Select>
        );
      case 'Number':
        return (
          <TextField
            fullWidth size="small" type="number" disabled={readOnly} placeholder={field.placeholder}
            value={typeof value === 'number' ? value : ''}
            slotProps={{ htmlInput: { step: field.decimal ? 'any' : 1 } }}
            onChange={(event) => set(event.target.value === '' ? undefined : Number(event.target.value))}
          />
        );
      default:
        return (
          <TextField
            fullWidth size="small" disabled={readOnly}
            placeholder={field.placeholder || (field.type === 'Barcode' ? t('Customization_Designer_Scan') : t('Customization_Designer_TypeHere'))}
            multiline={field.multiline} minRows={field.multiline ? 2 : undefined}
            value={typeof value === 'string' ? value : ''}
            slotProps={{
              htmlInput: { maxLength: field.charLimit },
              input: field.type === 'Barcode'
                ? { endAdornment: <InputAdornment position="end"><BarcodeIcon /></InputAdornment> }
                : undefined,
            }}
            onChange={(event) => set(event.target.value)}
          />
        );
    }
  };

  const end = field.type === 'Toggle'
    ? <Switch checked={Boolean(value)} disabled={readOnly} onChange={(_event, checked) => set(checked)} />
    : field.type === 'Range' && <Typography variant="body2" sx={{ fontWeight: 600 }}>{typeof value === 'number' ? value : field.min ?? 0}</Typography>;

  return (
    <Box>
      <FieldHeading field={field} languages={languages} required={required} readOnly={readOnly} marker={marker} trailing={end} />
      {field.type !== 'Toggle' && <Box sx={{ mt: 0.5 }}>{control()}</Box>}
      {field.help && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{field.help}</Typography>}
    </Box>
  );
};

export default memo(FieldInput);
